import { useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import Eyebrow from "../common/Eyebrow";

export const DATA_CENTER_FAQS = [
  {
    question: "What uptime SLA do QuantaRock data centers guarantee?",
    answer:
      "All QuantaRock facilities are backed by a 99.99% uptime SLA, delivered through N+1 redundant power, generator-backed UPS systems, and Tier-3 facility design across every one of our locations in India and internationally.",
  },
  {
    question: "How is physical and network security handled?",
    answer:
      "Every site combines biometric access control, 24/7 CCTV surveillance, and fire suppression systems with multi-ISP carrier-neutral connectivity and dark fiber options, so both the building and the network path to it are protected.",
  },
  {
    question: "What's the difference between colocation and a dedicated server with QuantaRock?",
    answer:
      "Colocation means you own the hardware and rent secure rack space, power, and connectivity in our facility. A dedicated server means QuantaRock owns and manages the hardware for you. Both run on the same Tier-3 infrastructure and 24/7 NOC support — the choice comes down to whether you want to manage your own equipment.",
  },
  {
    question: "How quickly can we deploy in a new city or country?",
    answer:
      "Most colocation and dedicated server deployments go live within 3-5 business days of signing, once rack space and connectivity are confirmed for your target city — including international locations like Dubai. Custom builds or bulk cabinet orders may take longer — our team will scope exact timelines in your quote.",
  },
  {
    question: "Do you support hybrid or multi-city, multi-country redundancy setups?",
    answer:
      "Yes. Many customers run primary infrastructure in one location with disaster recovery or backup capacity in another across our India and international network — including cross-border setups, such as India-based primary infrastructure backed up in Dubai — giving you geographic redundancy without managing multiple vendors. As we add new countries, this same redundancy model extends to them too.",
  },
  {
    question: "What certifications and compliance standards do you meet?",
    answer:
      "QuantaRock Systems is ISO 27001:2022 certified for information security management and ISO 9001:2015 certified for quality management, giving regulated and enterprise customers a documented compliance baseline across every facility.",
  },
];

const DataCenterFAQ = () => {
  const [openIndex, setOpenIndex] = useState(0);

  const toggle = (index) => {
    setOpenIndex((prev) => (prev === index ? null : index));
  };

  return (
    <section className="border-t border-slate-200 bg-slate-50 px-6 py-16 md:py-20">
      <div className="mx-auto max-w-4xl">
        <div className="text-center">
          <Eyebrow>FAQ</Eyebrow>
        </div>
        <h2 className="mx-auto mt-3 max-w-2xl text-center text-3xl font-bold tracking-tight text-[var(--website-primary-color)] md:text-4xl">
          Common questions about our data centers
        </h2>

        <div className="mt-10 space-y-4">
          {DATA_CENTER_FAQS.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div
                key={index}
                className={`overflow-hidden rounded-xl border shadow-sm transition-all duration-300 ${
                  isOpen
                    ? "border-[rgba(var(--website-secondary-rgb),0.6)] bg-white shadow-md"
                    : "border-slate-200 bg-white hover:border-[rgba(var(--website-secondary-rgb),0.4)]"
                }`}
              >
                  <button
                    type="button"
                    onClick={() => toggle(index)}
                    aria-expanded={isOpen}
                    aria-controls={`dc-faq-answer-${index}`}
                    className="flex w-full items-start gap-4 p-6 text-left"
                  >
                    <span
                      className={`flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full text-sm font-bold transition-colors duration-300 ${
                        isOpen
                          ? "bg-[var(--website-secondary-color)] text-[var(--website-primary-color)]"
                          : "bg-slate-100 text-[var(--website-primary-color)]"
                      }`}
                    >
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <h3 className="flex-1 pt-1 text-lg font-semibold text-[var(--website-primary-color)]">
                      {faq.question}
                    </h3>
                    <span
                      className={`mt-1 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full transition-all duration-300 ${
                        isOpen
                          ? "rotate-180 bg-[var(--website-secondary-color)] text-[var(--website-primary-color)]"
                          : "bg-slate-100 text-slate-500"
                      }`}
                    >
                      <FaChevronDown size={12} />
                    </span>
                  </button>
                  <div
                    id={`dc-faq-answer-${index}`}
                    className={`grid transition-all duration-300 ease-in-out ${
                      isOpen ? "grid-rows-[1fr] opacity-100" : "grid-rows-[0fr] opacity-0"
                    }`}
                  >
                    <div className="overflow-hidden">
                      <p className="ml-[4.25rem] mr-6 mb-6 border-l-2 border-[rgba(var(--website-secondary-rgb),0.5)] pl-4 leading-relaxed text-slate-600">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default DataCenterFAQ;
